import React from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowLeft, FileText, AlertCircle, CheckCircle, XCircle } from 'lucide-react';

const TermsOfService = () => {
  const navigate = useNavigate();

  const handleBack = () => {
    // If opened in new tab/window, close it; otherwise navigate back
    if (window.history.length <= 1 || window.opener) {
      window.close();
    } else {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-4 md:p-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <Button onClick={handleBack} variant="outline" size="sm">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back
          </Button>
          <Link to="/" className="inline-flex items-center rounded-sm text-sm font-medium text-blue-700 hover:underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-4 focus-visible:outline-blue-600">
            ← Back to Home
          </Link>
        </div>

        <Card>
          <CardHeader>
            <div className="flex items-center gap-3 mb-2">
              <FileText className="w-8 h-8 text-blue-600" />
              <CardTitle className="text-3xl" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
                Terms of Service
              </CardTitle>
            </div>
            <p className="text-sm text-gray-600">
              Last Updated: January 30, 2025
            </p>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Introduction */}
            <section>
              <h2 className="text-xl font-semibold mb-3">1. Acceptance of Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                Welcome to INSIGHT. By accessing or using our online counseling platform, you agree to be bound by these 
                Terms of Service. If you do not agree to these terms, please do not use our services.
              </p>
            </section>

            {/* Service Description */}
            <section>
              <h2 className="text-xl font-semibold mb-3">2. Service Description</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                INSIGHT is an online platform that connects clients with licensed psychologists for counseling and therapy services. 
                Our platform provides:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>5-phase psychological analysis</li>
                <li>Psychologist booking and consultation management</li>
                <li>Real-time Q&A messaging</li>
                <li>AI-powered Analysis generation</li>
                <li>Feedback and rating system</li>
              </ul>
              <div className="mt-4 bg-yellow-50 border-l-4 border-yellow-400 p-4">
                <p className="flex items-start gap-2 text-gray-700">
                  <AlertCircle className="w-5 h-5 text-yellow-600 flex-shrink-0 mt-0.5" />
                  <span>
                    <strong>Important:</strong> INSIGHT is NOT a diagnostic Model and should not be used for medical diagnosis. 
                    Our services are for counseling and therapy purposes only.
                  </span>
                </p>
              </div>
            </section>

            {/* User Accounts */}
            <section>
              <h2 className="text-xl font-semibold mb-3">3. User Accounts</h2>
              <div className="space-y-3 text-gray-700">
                <div>
                  <h3 className="font-semibold mb-2">Account Creation:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>You must provide accurate and complete information during registration</li>
                    <li>You are responsible for maintaining the confidentiality of your account credentials</li>
                    <li>You must be at least 18 years old to use this service</li>
                    <li>Psychologists must provide valid license/registration numbers</li>
                  </ul>
                </div>
                <div>
                  <h3 className="font-semibold mb-2">Account Security:</h3>
                  <ul className="list-disc pl-6 space-y-1">
                    <li>You are responsible for all activities under your account</li>
                    <li>Notify us immediately of any unauthorized access</li>
                    <li>Do not share your account credentials with others</li>
                  </ul>
                </div>
              </div>
            </section>

            {/* User Conduct */}
            <section>
              <h2 className="text-xl font-semibold mb-3">4. User Conduct</h2>
              <p className="text-gray-700 mb-3">You agree NOT to:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  Provide false or misleading information
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  Use the platform for illegal purposes
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  Harass, abuse, or harm other users
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  Attempt to gain unauthorized access to the platform
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  Share or distribute user content without permission
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <XCircle className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5" />
                  Use the platform for commercial purposes without authorization
                </li>
              </ul>
            </section>

            {/* Psychologist Responsibilities */}
            <section>
              <h2 className="text-xl font-semibold mb-3">5. Psychologist Responsibilities</h2>
              <p className="text-gray-700 mb-3">Psychologists using this platform must:</p>
              <ul className="space-y-2">
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Hold valid licenses and registrations
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Maintain professional standards and ethics
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Maintain client confidentiality
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Provide accurate and professional analyses
                </li>
                <li className="flex items-start gap-2 text-gray-700">
                  <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                  Respond to consultation requests within 48 hours
                </li>
              </ul>
            </section>

            {/* Intellectual Property */}
            <section>
              <h2 className="text-xl font-semibold mb-3">6. Intellectual Property</h2>
              <p className="text-gray-700 leading-relaxed">
                All content on the INSIGHT platform, including text, graphics, logos, and software, is the property of 
                INSIGHT or its licensors and is protected by copyright and other intellectual property laws. Your analysis 
                data remains your property, but you grant us a license to use it to provide our services.
              </p>
            </section>

            {/* Confidentiality */}
            <section>
              <h2 className="text-xl font-semibold mb-3">7. Confidentiality</h2>
              <p className="text-gray-700 leading-relaxed">
                We take confidentiality seriously. All communications between clients and psychologists are confidential, 
                except where disclosure is required by law or to prevent harm. Please refer to our Privacy Policy for 
                details on how we handle your data.
              </p>
            </section>

            {/* Limitations */}
            <section>
              <h2 className="text-xl font-semibold mb-3">8. Disclaimer of Warranties</h2>
              <div className="bg-red-50 border-l-4 border-red-400 p-4">
                <p className="text-gray-700 leading-relaxed">
                  THE SERVICE IS PROVIDED "AS IS" WITHOUT WARRANTIES OF ANY KIND. WE DO NOT GUARANTEE THAT THE SERVICE 
                  WILL BE UNINTERRUPTED, ERROR-FREE, OR SECURE. WE ARE NOT RESPONSIBLE FOR THE PROFESSIONAL CONDUCT OF 
                  PSYCHOLOGISTS OR THE OUTCOMES OF THERAPY SESSIONS.
                </p>
              </div>
            </section>

            {/* Limitation of Liability */}
            <section>
              <h2 className="text-xl font-semibold mb-3">9. Limitation of Liability</h2>
              <p className="text-gray-700 leading-relaxed">
                TO THE MAXIMUM EXTENT PERMITTED BY LAW, INSIGHT SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, 
                CONSEQUENTIAL, OR PUNITIVE DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY SHALL NOT 
                EXCEED THE AMOUNT YOU PAID FOR THE SERVICE IN THE PAST 12 MONTHS.
              </p>
            </section>

            {/* Cancellation */}
            <section>
              <h2 className="text-xl font-semibold mb-3">10. Cancellation and Refunds</h2>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>Clients may cancel appointments up to 24 hours before the scheduled time</li>
                <li>Consultations not responded to within 48 hours are automatically cancelled</li>
                <li>Analysis validity is 90 days from the first completed session</li>
                <li>Refund policies will be determined on a case-by-case basis</li>
              </ul>
            </section>

            {/* Account Termination */}
            <section>
              <h2 className="text-xl font-semibold mb-3">11. Account Termination</h2>
              <p className="text-gray-700 leading-relaxed mb-3">
                We reserve the right to suspend or terminate your account if:
              </p>
              <ul className="list-disc pl-6 space-y-2 text-gray-700">
                <li>You violate these Terms of Service</li>
                <li>You provide false information</li>
                <li>You engage in fraudulent or illegal activities</li>
                <li>Your account remains inactive for an extended period</li>
              </ul>
              <p className="text-gray-700 leading-relaxed mt-3">
                You may delete your account at any time from your account settings. Upon deletion, your personal data 
                will be anonymized as per our Privacy Policy.
              </p>
            </section>

            {/* Changes to Terms */}
            <section>
              <h2 className="text-xl font-semibold mb-3">12. Changes to Terms</h2>
              <p className="text-gray-700 leading-relaxed">
                We may update these Terms of Service from time to time. We will notify you of significant changes by 
                email or through the platform. Your continued use of the service after changes constitutes acceptance 
                of the new terms.
              </p>
            </section>

            {/* Governing Law */}
            <section>
              <h2 className="text-xl font-semibold mb-3">13. Governing Law</h2>
              <p className="text-gray-700 leading-relaxed">
                These Terms of Service are governed by the laws of India. Any disputes arising from these terms or your 
                use of the service shall be subject to the exclusive jurisdiction of the courts in India.
              </p>
            </section>

            {/* Dispute Resolution */}
            <section>
              <h2 className="text-xl font-semibold mb-3">14. Dispute Resolution</h2>
              <p className="text-gray-700 leading-relaxed">
                In the event of any dispute, we encourage you to contact us first to resolve the issue informally. 
                If a dispute cannot be resolved, it will be settled through arbitration in accordance with Indian law.
              </p>
            </section>

            {/* Contact Information */}
            <section className="bg-blue-50 p-4 rounded-lg">
              <h2 className="text-xl font-semibold mb-3">15. Contact Us</h2>
              <p className="text-gray-700 leading-relaxed mb-2">
                If you have any questions about these Terms of Service, please contact us at:
              </p>
              <p className="text-gray-700">
                <strong>Email:</strong> support@insight-platform.com<br />
                <strong>Address:</strong> INSIGHT Platform, India
              </p>
            </section>

            {/* Acceptance */}
            <section className="border-t pt-6">
              <h2 className="text-xl font-semibold mb-3">Acceptance</h2>
              <p className="text-gray-700 leading-relaxed">
                By using INSIGHT, you acknowledge that you have read, understood, and agree to be bound by these 
                Terms of Service and our Privacy Policy.
              </p>
            </section>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default TermsOfService;
